import { useCallback, useEffect, useState } from 'react';
import type { Person, PeriodMonth } from '../types/timesheet.types';
import { TimesheetStatus } from '../types/timesheet.types';
import { useConnector } from '../contexts/ConnectorContext';
import { addMonths, getMonthBounds, getPeriodMonth } from '../utils/dateUtils';
import { errorLogger } from '../utils/errorLogger';

export interface IncompleteTimesheetRow {
  personId: string;
  personName: string;
  personEmail: string;
  department: string;
  lineManager: string;
  lineManagerEmail: string;
  /** The period this row relates to — may differ from the selected periodMonth when
   *  previous-month stragglers are surfaced after the 2nd of the current month. */
  rowPeriodMonth: PeriodMonth;
  /** Days since the end of `rowPeriodMonth` (0 if still in-period). */
  daysOverdue: number;
  /** True while the exemption window covers today. */
  isExempt: boolean;
  /** Label describing the most recent submission, or 'Never'. */
  lastSubmittedLabel: string;
}

export interface UseIncompleteTimesheetsResult {
  rows: IncompleteTimesheetRow[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

const SUBMITTED_STATUSES: readonly TimesheetStatus[] = [
  TimesheetStatus.Submitted,
  TimesheetStatus.ManagerApproved,
  TimesheetStatus.SuperApproved,
  TimesheetStatus.Locked,
];

function isActiveExemption(p: Person, today: Date): boolean {
  if (!p.exemptFromReminders) return false;
  const start = p.exemptionStart ? new Date(p.exemptionStart) : null;
  const end = p.exemptionEnd ? new Date(p.exemptionEnd) : null;
  if (start && today < start) return false;
  if (end && today > end) return false;
  return true;
}

function daysOverdueFor(periodMonth: PeriodMonth, today: Date): number {
  const { end } = getMonthBounds(periodMonth);
  const diff = Math.floor((today.getTime() - end.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

/**
 * Identifies people who have not submitted a timesheet for the previous calendar month.
 *
 * Only active from the 3rd of the current month onwards — before then, returns an empty
 * list (staff still have the first two days of the new month to submit).
 *
 * "Incomplete" = active person who either
 *  (a) has zero entries in Submitted / Manager Approved / Super Approved / Locked, or
 *  (b) has at least one entry in Rejected status — the month was bounced back and needs
 *      to be resubmitted.
 *
 * Exempt people are included but marked isExempt=true — they won't receive automated
 * reminders but a super approver can still send a manual notification.
 */
export function useIncompleteTimesheets(): UseIncompleteTimesheetsResult {
  const connector = useConnector();
  const [rows, setRows] = useState<IncompleteTimesheetRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const today = new Date();
      const prevCalMonth = addMonths(getPeriodMonth(today), -1);

      // Before the 3rd of the month, staff still have time — return nothing.
      if (today.getDate() < 3) {
        setRows([]);
        return;
      }

      const [activePeople, entries] = await Promise.all([
        connector.getPeople({ active: true }),
        connector.getTimeEntries({ periodMonth: prevCalMonth }),
      ]);

      const submittedEmails = new Set(
        entries
          .filter(e => SUBMITTED_STATUSES.includes(e.status))
          .map(e => e.personEmail.toLowerCase())
      );
      const rejectedEmails = new Set(
        entries
          .filter(e => e.status === TimesheetStatus.Rejected)
          .map(e => e.personEmail.toLowerCase())
      );

      const overdue = daysOverdueFor(prevCalMonth, today);

      const result: IncompleteTimesheetRow[] = activePeople
        .filter(p => !submittedEmails.has(p.m365Email.toLowerCase()) || rejectedEmails.has(p.m365Email.toLowerCase()))
        .map(p => ({
          personId: p.id,
          personName: p.fullName,
          personEmail: p.m365Email,
          department: p.department,
          lineManager: p.lineManager || '—',
          lineManagerEmail: p.lineManagerEmail,
          rowPeriodMonth: prevCalMonth,
          daysOverdue: overdue,
          isExempt: isActiveExemption(p, today),
          lastSubmittedLabel: '—',
        }))
        .sort((a, b) => a.personName.localeCompare(b.personName));

      setRows(result);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      errorLogger.log(e, { component: 'useIncompleteTimesheets', action: 'load' });
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [connector]);

  useEffect(() => { void load(); }, [load]);

  return { rows, isLoading, error, refresh: load };
}
